import {get_natural_height} from 'lib/utilities';
import gsap from 'gsap';

interface CollapsibleOptions {
    duration?: number;
    to?: number;
    from?: number;
    fade?: boolean;
    tweenOptions?: gsap.TweenVars;
    class?: string;
}

type FinalOptions = Required<CollapsibleOptions>;

function resolveOptions(opts: CollapsibleOptions): FinalOptions {
    return {
        fade: true,
        to: 0,
        from: 0,
        duration: 0.5,
        tweenOptions: {},
        class: 'is-collapsed',
        ...opts
    }
}

const tweens = new Map<HTMLElement, gsap.core.Tween>();

function kill(element: HTMLElement) {
    tweens.get(element)?.kill();

    tweens.delete(element);
}

function fromTo(element: HTMLElement, from: gsap.TweenVars, to: gsap.TweenVars) {
    const tween = gsap.fromTo(element, from, to);

    tweens.set(element, tween);

    return tween;
}

export function toggle(element: HTMLElement, opts: CollapsibleOptions = {}) {
    const options = resolveOptions(opts);

    if (element.classList.contains(options.class)) {
        expand(element, opts);
    } else {
        collapse(element, opts);
    }
}

export function collapse(element: HTMLElement, opts: CollapsibleOptions = {}) {
    kill(element);

    const options = resolveOptions({
        from: element.offsetHeight,
        to: 0,
        ...opts
    });

    element.classList.add(options.class);

    return fromTo(element, {
        height: options.from,
        opacity: options.fade ? 1 : undefined
    }, {
        duration: options.duration,
        ...options.tweenOptions,
        height: options.to,
        opacity: options.fade ? 0 : undefined
    });
}

export function expand(element: HTMLElement, opts: CollapsibleOptions = {}) {
    kill(element);

    const options = resolveOptions({
        from: element.offsetHeight,
        to: get_natural_height(element),
        ...opts
    });

    element.classList.remove(options.class);

    return fromTo(element, {
        height: options.from,
        opacity: options.fade ? 0 : undefined
    }, {
        duration: options.duration,
        ...options.tweenOptions,
        height: options.to,
        opacity: options.fade ? 1 : undefined,
        onComplete() {
            gsap.set(element, {
                height: 'auto'
            })
        }
    });
}
